/**
 * This is the Best Bank Offer cell contents.
 *
 * Note BankOfferCell won't render this component for cash or payments with an empty `offers` array,
 * will render a DisabledCell instead
 *
 * The styling works like:
 * If there is more than one offer on this payment type,
 *      display a (+) icon on the right edge of the cell
 *      (vertically centered, 1em padded from right)
 * Bank "stuff" takes up the rest of the cell, vertically and horizontally centered.
 *      If the offer is a loan or a lease-in-Canada, display APR on a line of its own
 *      If the offer has a lender logo, display the logo
 *      If the logo has any error (404 is typical), replace the Img with a Div containing lender name
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { Img } from "react-image";
import { connect } from "react-redux";
import DrawerToggle from "Shared/DrawerToggle";
import styled from "styled-components";
import { PositionedCellPlusButton } from "src/components/MyGarage/TableCompare/Cells/CellPlusButton";
import { ApplicationStore } from "src/store";
import { getCountry } from "src/store/selectors/account";
import { getBankLogoSrc } from "src/store/selectors/banks";
import {
    getDealerOfferInterestRateByVin,
    getDealerOfferLenderByVin,
    getDealerOfferTypeByVin,
} from "src/store/selectors/dealerOffer";
import {
    getInterestRate,
    getLender,
    getSelectedPaymentOffers,
    getSelectedPaymentType,
} from "src/store/selectors/vehicles";
import { formatNumberAsPercentage } from "src/utils";

const StyledDrawerToggle = styled(DrawerToggle)`
    color: inherit;
    height: 100%;
    width: 100%;
`;

export const APRandLender = styled("div")`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    align-items: center;
`;

export const LogoImg = styled(Img)`
    max-width: 100px;
`;

export const LenderName = styled("div")`
    max-height: 100%;
    overflow: hidden;
    text-align: center;
`;

/**
 * Note, there are a LOT of properties in BaseCellProps, but we're trying to make this
 * component more self-contained and less dependent on the Rube-Goldberg machine in VehicleFeatures
 */
interface Props {
    vin: string;
}

const BankOfferContents = ({
    country,
    interest_rate,
    lender,
    logoSrc,
    paymentType,
    shouldOpenModal,
    vin,
}: Props & ReturnType<typeof mapStateToProps>) => {
    const { t } = useTranslation();

    // In the US we only display loan APRs. In Canada, we also display lease APRs.
    const shouldDisplayAPR = paymentType === "loan" || country === "CA";

    const cellContents = (
        <APRandLender>
            {shouldDisplayAPR && <div>{formatNumberAsPercentage(interest_rate)}</div>}
            <LogoImg src={logoSrc} unloader={<LenderName>{lender}</LenderName>} />
        </APRandLender>
    );
    if (!shouldOpenModal) {
        return cellContents;
    }
    return (
        <StyledDrawerToggle
            path={{
                pathname: "/my-garage/bank-offers",
                state: { vin },
            }}
            title={t("MyGarage.TableCompare.bestBankOfferLabel")}
        >
            <>
                <PositionedCellPlusButton />
                {cellContents}
            </>
        </StyledDrawerToggle>
    );
};

const mapStateToProps = (state: ApplicationStore, { vin }: Props) => {
    const dealerOfferType = getDealerOfferTypeByVin(state, vin);
    let lender;
    if (dealerOfferType) {
        lender = getDealerOfferLenderByVin(state, vin) || "";
        return {
            country: getCountry(state),
            interest_rate: getDealerOfferInterestRateByVin(state, vin),
            lender,
            logoSrc: getBankLogoSrc(state, lender),
            shouldOpenModal: false,
            paymentType: dealerOfferType,
        };
    }

    lender = getLender(state, vin) || "";
    return {
        country: getCountry(state),
        interest_rate: getInterestRate(state, vin),
        lender,
        logoSrc: getBankLogoSrc(state, lender),
        shouldOpenModal: getSelectedPaymentOffers(state, vin).length > 1,
        paymentType: getSelectedPaymentType(state, vin),
    };
};

export default connect(mapStateToProps)(BankOfferContents);
